import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, ArrowRight, Facebook, Instagram, Linkedin, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// HubSpot Configuration
const HUBSPOT_PORTAL_ID = "244712680";
const HUBSPOT_FORM_GUID = "c2843802-1cee-4dda-99c9-94acca3b5449";

export const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const submitToHubSpot = async (data) => {
    const hubspotEndpoint = `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_GUID}`;
    
    const hubspotData = {
      fields: [
        { name: "firstname", value: data.name },
        { name: "email", value: data.email },
        { name: "phone", value: data.phone || "" },
        { name: "message", value: data.message }
      ],
      context: {
        pageUri: window.location.href,
        pageName: "Next Chapter Moves NC - Contact"
      }
    };

    const response = await fetch(hubspotEndpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(hubspotData)
    });

    if (!response.ok) {
      throw new Error("HubSpot submission failed");
    }

    return response.json();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Submit to both backend and HubSpot in parallel
      await Promise.all([
        axios.post(`${API}/contact`, formData),
        submitToHubSpot(formData)
      ]);
      
      setIsSubmitted(true);
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error("Failed to send message", {
        description: "Please try again or contact us directly."
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSendAnother = () => {
    setIsSubmitted(false);
  };

  return (
    <section id="contact" className="section bg-[#F8FAFC]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <div className="animate-hidden animate-slideInLeft">
            <div className="gold-line" />
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-6 font-serif">
              Let's Talk
            </h2>
            <p className="text-lg text-[#475569] mb-10 leading-relaxed">
              Ready to explore your options? Have questions about the process? We're here to help — with no pressure and no obligation.
            </p>

            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-[#0F172A]" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-[#0F172A] mb-1">Location</h4>
                  <p className="text-[#475569]">Knightdale, North Carolina</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                  <Mail className="text-[#0F172A]" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-[#0F172A] mb-1">Email</h4>
                  <a href="mailto:contact@nextchaptermoves.com" className="text-[#D4AF37] hover:underline">info@nextchaptermoves.net

                  </a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                  <Phone className="text-[#0F172A]" size={24} />
                </div>
                <div>
                  <h4 className="font-semibold text-[#0F172A] mb-1">Phone</h4>
                  <a href="tel:919-879-5636" className="text-[#D4AF37] hover:underline">
                    (919) 879-5636
                  </a>
                </div>
              </div>

              {/* Social Media Links */}
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-[#D4AF37] flex items-center justify-center flex-shrink-0">
                  <svg className="text-[#0F172A] w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2C6.477 2 2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.879V14.89h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.989C18.343 21.129 22 16.99 22 12c0-5.523-4.477-10-10-10z" />
                  </svg>
                </div>
                <div>
                  <h4 className="font-semibold text-[#0F172A] mb-2">Connect With Us</h4>
                  <div className="flex items-center gap-4">
                    <a
                      href="https://facebook.com/iris.mckoy.1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-[#0F172A] hover:bg-[#D4AF37] flex items-center justify-center transition-colors duration-300 group"
                      aria-label="Facebook"
                      data-testid="social-facebook">

                      <Facebook className="text-white group-hover:text-[#0F172A]" size={20} />
                    </a>
                    <a
                      href="https://instagram.com/iristherealtor"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-[#0F172A] hover:bg-[#D4AF37] flex items-center justify-center transition-colors duration-300 group"
                      aria-label="Instagram"
                      data-testid="social-instagram">

                      <Instagram className="text-white group-hover:text-[#0F172A]" size={20} />
                    </a>
                    <a
                      href="https://tiktok.com/@iristherealtor5"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-[#0F172A] hover:bg-[#D4AF37] flex items-center justify-center transition-colors duration-300 group"
                      aria-label="TikTok"
                      data-testid="social-tiktok">

                      <svg className="text-white group-hover:text-[#0F172A] w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z" />
                      </svg>
                    </a>
                    <a
                      href="https://linkedin.com/in/iris-s-mckoy-37334321/"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-10 h-10 bg-[#0F172A] hover:bg-[#D4AF37] flex items-center justify-center transition-colors duration-300 group"
                      aria-label="LinkedIn"
                      data-testid="social-linkedin">

                      <Linkedin className="text-white group-hover:text-[#0F172A]" size={20} />
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-12 p-6 bg-white border-l-4 border-[#D4AF37]">
              <p className="text-[#0F172A] font-medium italic font-serif text-lg">
                "Your next chapter starts with a simple conversation."
              </p>
            </div>
          </div>

          <div className="animate-hidden animate-slideInRight animate-delay-200">
            <div className="bg-white p-8 md:p-12 shadow-lg">
              {isSubmitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-[#D4AF37] flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="text-[#0F172A]" size={32} />
                  </div>
                  <h3 className="text-2xl font-bold text-[#0F172A] mb-4 font-serif">
                    Thank You!
                  </h3>
                  <p className="text-[#475569] text-lg mb-6 leading-relaxed">
                    We appreciate your interest in Next Chapter Moves NC. We'll review your message and get back to you as soon as possible.
                  </p>
                  <p className="text-[#94A3B8] mb-8">
                    Typically within 24 hours
                  </p>
                  <button
                    onClick={handleSendAnother}
                    className="btn-outline"
                    data-testid="send-another-button"
                  >
                    Send Another Message
                  </button>
                </div>
              ) : (
                <>
                  <h3 className="text-2xl font-bold text-[#0F172A] mb-6 font-serif">
                    Send a Message
                  </h3>
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-[#0F172A] mb-2">
                        Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="form-input"
                        placeholder="Your name"
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-[#0F172A] mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="form-input"
                        placeholder="your@email.com"
                        data-testid="contact-email-input"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-[#0F172A] mb-2">
                        Phone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input"
                        placeholder="(555) 123-4567"
                        data-testid="contact-phone-input"
                      />
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-[#0F172A] mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="form-input resize-none"
                        placeholder="Tell us about your situation or questions..."
                        data-testid="contact-message-input"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="btn-primary w-full justify-center"
                      data-testid="contact-submit-button"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner" />
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <ArrowRight size={18} />
                        </>
                      )}
                    </button>

                    <p className="text-sm text-[#94A3B8] text-center">
                      We'll respond within 24 hours. No spam, no pressure.
                    </p>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>);

};